<template>
  <div>
    <b-card no-body>
      <b-table
        id="filled_forms"
        ref="table"
        class="bg-white"
        responsive
        show-empty
        :per-page="perPage"
        :current-page="currentPage"
        :fields="fields"
        :items="filteredItems"
      >
        <template v-slot:empty>
          <p align="center">
            <span v-if="!busy">{{ trans('none') }}</span>
            <span v-if="busy">
              <i class="fa fa-fw fa-circle-notch fa-spin" v-if="busy"></i>
              {{ trans('please_wait') }}
            </span>
          </p>
        </template>
        <template v-slot:head(recipient_form_name)="data">
          {{ data.label }}
          <b-button variant="link" size="sm" class="p-0 ml-1" @click="fetch" :disabled="busy">
              <i :class="`fa fa-fw ${busy ? 'fa-circle-notch fa-spin' : 'fa-redo'}`"></i>
          </b-button>
        </template>
        <template v-slot:cell(name)="data">
          <small class="d-block">
            {{ compact([
              data.value,
              data.item.email,
              data.item.mobile,
            ]).join(', ') }}
          </small>
        </template>
        <template v-slot:cell(children)="data">
          <small class="d-block" v-for="(child, index) in data.value" :key="index">
            {{
              compact([
                child.name,
                child.gender ? trans('gender_'+child.gender) : null,
                child.date_of_birth ? `${child.date_of_birth} (${moment().diff(child.date_of_birth, 'years')})` : null,
                child.grade ? `${trans('grade')} ${child.grade}` : null,
                child.email,
                child.mobile
              ]).join(', ')
            }}
          </small>
        </template>
        <template v-slot:cell(participant)="data">
          <small>
            {{
              compact([
                data.value.name,
                data.value.gender ? trans('gender_'+data.value.gender) : null,
                data.value.date_of_birth ? `${data.value.date_of_birth} (${moment().diff(data.value.date_of_birth, 'years')})` : null,
              ]).join(', ')
            }}
          </small>
        </template>
        <template v-slot:cell(media_answers)="data">
          <div v-for="answer in data.value" :key="answer.id" class="mb-2">
            <small class="d-block text-muted">{{ answer.label }}</small>
            <div v-for="media in answer.json" :key="media.umid">
              <b-button v-if="answer.type == 'images'" :href="mediaUrl(answer, media.umid)" target="_blank" variant="link" size="sm" class="p-0 text-left">
                <i class="fa fa-fw fa-image"></i> {{ media.name }}
              </b-button>
              <b-button v-if="answer.type == 'videos' && !media.deleted_at" @click="onClickVideo(answer, media)" variant="link" size="sm" class="p-0 text-left">
                <i class="fa fa-fw fa-video"></i> {{ media.name }}
              </b-button>
            </div>
          </div>
        </template>
        <template v-slot:cell(borough)="data">
          <small class="d-block">{{ data.value }}</small>
        </template>
        <template v-slot:cell(sport)="data">
          <small class="d-block">{{ data.value }}</small>
        </template>
        <template v-slot:cell(filled_at)="data">
          <small class="d-block">{{ data.value }}</small>
        </template>
        <template #cell(action)="data">
          <b-dropdown
            v-if="showActionsMenu(data.item)"
            variant="link"
            size="sm"
            class="p-0"
            toggle-class="text-muted"
            boundary="window"
            right
            :disabled="busy"
          >
            <template #button-content>
              <i class="fa fa-fw fa-ellipsis-h"></i>
            </template>
            <b-dropdown-item
              :href="maybeAddSession(`/org/${organization.id}/client/filled_form_pdf/${data.item.id}`, organization)"
              target="_blank"
            >
              <i class="fa fa-fw fa-file-pdf"></i>
              {{ trans('download_pdf') }}
            </b-dropdown-item>
            <b-dropdown-item
              v-if="canModify(data.item)"
              @click="onViewModifyClone($event, data.item)"
            >
              <i class="fa fa-fw fa-edit"></i>
              {{ trans('modify') }}
            </b-dropdown-item>
            <b-dropdown-item
              v-if="canClone(data.item)"
              @click="onViewModifyClone($event, data.item, true)"
            >
              <i class="fa fa-fw fa-copy"></i>
              {{ trans('duplicate') }}
            </b-dropdown-item>
            <b-dropdown-item
              v-if="canDelete(data.item)"
              @click="onClickDelete($event, data.item)"
            >
              <span class="text-danger">
                <i class="fa fa-fw fa-trash"></i>
                {{ trans('delete') }}
              </span>
            </b-dropdown-item>
          </b-dropdown>
          <b-link
            v-if="!showActionsMenu(data.item)"
            :href="maybeAddSession(`/org/${organization.id}/client/filled_form_pdf/${data.item.id}`, organization)"
            target="_blank"
            :title="trans('download_pdf')"
          >
            <i class="fa fa-fw fa-file-pdf"></i>
          </b-link>
        </template>
      </b-table>
      <b-pagination
        :total-rows="filteredItems.length"
        :per-page="perPage"
        v-model="currentPage"
        aria-controls="filled_forms"
        align="center"
      ></b-pagination>
    </b-card>
    <b-modal
      id="filled-forms-video-modal"
      :title="media ? media.name : null"
      ok-only
      :ok-title="trans('close')"
      size="lg"
    >
      <div v-if="!!media">
        <video
          :src="media.url"
          controls
          class="w-100"
        />
      </div>
      <div v-if="!!media">
        <b-button variant="link" size="sm" class="text-muted" @click="video_only_audio = !video_only_audio">
          <i class="fa fa-fw fa-info-circle"></i>
          {{ trans('video_only_audio') }}
        </b-button>
        <b-alert variant="danger" :show="video_only_audio" class="mx-2">
          {{ trans('video_only_audio_note') }}
        </b-alert>
        <b-img v-if="video_only_audio" :src="downloadVideoInstructionsImageUrl" fluid />
      </div>
      <div v-if="!media">
        <p class="lead text-center my-4">
          <i class="fa fa-fw fa-circle-notch fa-spin"></i>
          {{ trans('please_wait') }}
        </p>
      </div>
    </b-modal>
    <client-form-iframe-modal
      :organization="organization"
      :upid="item ? item.recipient_form_upid : null"
      :urid="item ? item.urid || null : null"
      :title="trans(!!item && !!item.urid ? 'modify' : 'new')"
      @hidden="fetch"
    ></client-form-iframe-modal>
  </div>
</template>

<script>
  import ClientFormIframeModal from './ClientFormIframeModal'

  function hasThing(value, things) {
    if (!value) return false
    value = value.toLowerCase()
    return _.reduce(things, (has, thing) => {
      if (value.indexOf(thing.toLowerCase()) >= 0) has = true
      return has
    }, false)
  }

  export default {
    props: {
      organization: Object,
      client: Object,
      clientTab: Object, // Can be null
    },
    components: {
      ClientFormIframeModal,
    },
    data() {
      return {
        trans: trans,
        compact: _.compact,
        moment: moment,
        maybeAddSession: _.maybeAddSession,
        busy: false,
        error: null,
        items: null,
        item: null,
        perPage: 10,
        currentPage: 1,
        totalRows: 0,
        media: null,
        video_only_audio: false,
      }
    },
    computed: {
      hasDifferentRespondent() {
        return _.reduce(this.items || [], (has, item) => {
          if (this.client.emails.indexOf(item.email) === -1) has = true
          return has
        }, false)
      },
      hasChildren() {
        return _.reduce(this.items || [], (has, item) => {
          if (item.children && item.children.length > 0) has = true
          return has
        }, false)
      },
      hasParticipant() {
        return _.reduce(this.items || [], (has, item) => {
          if (!!item.participant) has = true
          return has
        }, false)
      },
      hasMedia() {
        return _.reduce(this.items || [], (has, item) => {
          if (item.media_answers && item.media_answers.length > 0) has = true
          return has
        }, false)
      },
      fields() {
        return _.compact([
          {
            key: 'recipient_form_name',
            label: trans('form'),
          },
          this.hasDifferentRespondent ? {key: 'name', label: trans('respondent')} : null,
          this.hasChildren ? {key: 'children', label: trans('children')} : null,
          this.hasParticipant ? {key: 'participant', label: trans('participant')} : null,
          this.hasMedia ? {key: 'media_answers', label: trans('media')} : null,
          {
            key: 'filled_at',
            label: trans('date'),
            formatter(value) {
              return moment(value).format('LLL')
            },
            sortable: true,
            sortDirection: 'desc',
          },
          {
            key: 'action',
            label: trans('operation'),
          },
        ])
      },
      filteredItems() {
        return _.reduce(this.items || [], (items, item) => {
          let pass = true
          if (pass) items.push(item)
          return items
        }, [])
      },
      downloadVideoInstructionsImageUrl() {
        return `https://activitymessenger-assets-ca.s3.ca-central-1.amazonaws.com/help/video-not-playing-${window.lang}.png`
      },
    },
    methods: {
      canModify(item) {
        if (!this.clientTab) return false
        return _.reduce(this.clientTab.recipient_forms, (allowed, form) => {
          if (form.id == item.recipient_form_id && form.pivot.allow_modify) allowed = true
          return allowed
        }, false)
      },
      canClone(item) {
        if (!this.clientTab) return false
        return _.reduce(this.clientTab.recipient_forms, (allowed, form) => {
          if (form.id == item.recipient_form_id && form.pivot.allow_clone) allowed = true
          return allowed
        }, false)
      },
      canDelete(item) {
        if (!this.clientTab) return false
        return _.reduce(this.clientTab.recipient_forms, (allowed, form) => {
          if (form.id == item.recipient_form_id && form.pivot.allow_archive) allowed = true
          return allowed
        }, false)
      },
      showActionsMenu(item) {
        return this.canModify(item) || this.canClone(item) || this.canDelete(item)
      },
      onClickDelete(e, item) {
        e.preventDefault()
        let message = trans('delete_confirm')
        if (this.hasClass) message = trans('delete_confirm')
        this.$bvModal.msgBoxConfirm(message, {
          title: item.participant ? item.participant.name : '',
          okTitle: trans('delete'),
          okVariant: 'danger',
          cancelTitle: trans('cancel'),
        })
        .then(confirmed => {
          if (!confirmed) return
          const url = `/org/${this.organization.id}/client/filled_forms/${item.recipient_id}`
          axios.delete(url)
          .then(response => {
            this.$bvToast.toast(response.data.message, {
              title: trans('success'),
              variant: 'success',
              solid: true,
            })
            this.fetch()
          })
          .catch(error => {
            this.error = _.errorFromAjax(error)
            _.toastError(this, this.error)
          })
        })
      },
      onViewModifyClone(e, item, clone) {
        e.preventDefault()
        this.item = item

        if (this.item.urid) {
          this.$bvModal.show('client-form-iframe-modal')
          return
        }

        this.busy = true
        this.error = null
        let url = `/org/${this.organization.id}/client/${clone ? 'urid_for_cloned_recipient' : 'urid_for_recipient'}/${this.item.recipient_id}`
        axios.get(url)
        .then(response => {
          this.busy = false
          this.$set(this.item, 'urid', response.data.urid)
          this.$bvModal.show('client-form-iframe-modal')
        })
        .catch(error => {
          this.busy = false
          this.error = _.errorFromAjax(error)
          this.$bvToast.toast(this.error, {
            title: trans('error'),
            variant: 'danger',
            solid: true,
          })
        })
      },
      onClickVideo(answer, media) {
        this.media = _.assign({}, media, {url: this.mediaUrl(answer, media.umid)})
        this.$bvModal.show('filled-forms-video-modal')
      },
      mediaUrl(answer, umid) {
        return _.maybeAddSession(`/org/${this.organization.id}/client/answer/${answer.id}/media/${umid}`, this.organization)
      },
      fetch() {
        this.busy = true
        this.error = null
        this.items = null
        let query = `?page=${this.currentPage}&per_page=${this.perPage}`
        let url = `/org/${this.organization.id}/client/filled_forms${query}`
        return axios.get(url)
        .then(response => {
          this.busy = false
          this.items = response.data
          this.totalRows = this.items.length
        })
        .catch(error => {
          this.busy = false
          this.error = _.errorFromAjax(error)
          this.$bvToast.toast(this.error, {
            title: trans('error'),
            variant: 'danger',
            solid: true,
          })
          this.items = []
        })
      },
    },
    mounted() {
      window.myFilledForms = this
      this.fetch()
    },
  }
</script>